import { Container, ContainerChild, Graphics, Text } from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetProgressBar,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { TimerService } from "../Services/TimerService";

import { getProgressBarDesign } from "../../../../data/progressBarDesignsCatalog";

import { ProgressBarDesignRenderer } from "./ProgressBarDesignRenderer";

const parseHex = (input: string | undefined, fallback: number): number => {
  if (!input) return fallback;
  const m = input.match(/^#?([0-9a-f]{6})$/i);
  return m ? parseInt(m[1], 16) : fallback;
};

const parseAlpha = (input: string | undefined): number => {
  if (!input) return 1;
  const rgba = input.match(
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*([\d.]+))?\s*\)$/i,
  );
  if (rgba && rgba[1] !== undefined) return parseFloat(rgba[1]);
  return 1;
};

const fillFromColor = (input: string | undefined, fallback: number) => {
  // CSS `transparent` is equivalent to rgba(0,0,0,0). Without this guard
  // the parseHex regex doesn't match, fallback is used (often a visible
  // dark colour), and parseAlpha returns the default `1` → Pixi draws a
  // fully opaque black/dark rectangle where the DOM shows nothing.
  if (input === "transparent") return { color: 0, alpha: 0 };
  return { color: parseHex(input, fallback), alpha: parseAlpha(input) };
};

/**
 * Solid semi-transparent white used for the optional gloss overlay.
 * Was previously a vertical alpha gradient (white α 0.85 → transparent)
 * but that ran into the same CSS↔Pixi parity issues as the bar fill
 * gradient — color-space interpolation, stop positioning, alpha
 * compositing all differ enough between the renderers to be visibly
 * inconsistent. A flat white at α 0.32 reads as a soft top sheen when
 * confined to the top 30% of the bar (matches the original Y2K
 * highlight intent without the gradient brittleness).
 */
const GLOSS_FILL = { color: 0xffffff, alpha: 0.32 } as const;

const formatLabel = (
  template: string | undefined,
  value: number,
  pct: number,
  showPercent: boolean | undefined,
): string => {
  if (!template) return "";
  const filled = template
    .replace(/\{percent\}/g, String(pct))
    .replace(/\{value\}/g, String(value));
  const hadPlaceholder = filled !== template;
  if (!hadPlaceholder && showPercent) return `${filled} · ${pct}%`;
  return filled;
};

const computeValue = (
  o: IStudioWidgetProgressBar["options"],
  max: number,
  nowMs: number,
): number => {
  if (!o.targetDate) return 0;
  const target = new Date(o.targetDate).getTime();
  if (Number.isNaN(target)) return 0;
  const start = o.startDate
    ? new Date(o.startDate).getTime()
    : target - 5 * 60 * 1000;
  if (Number.isNaN(start) || target <= start) return 0;
  const ratio = (nowMs - start) / (target - start);
  return Math.max(0, Math.min(max, ratio * max));
};

interface WidgetState {
  destination: { x: number; y: number; width: number; height: number };
  options: IStudioWidgetProgressBar["options"];
  fillBar: Graphics;
  gloss?: Graphics;
  labelText?: Text;
  lastRatio: number;
  timerCallback?: (time: number) => void;
}

/**
 * Pixi renderer for the generic progress-bar widget. Visual matches the
 * DOM preview. When `mode === "time"`, subscribes to `TimerService` so
 * the fill width animates as wall-clock time progresses between
 * `startDate` and `targetDate`. In manual mode it draws once and stays
 * static — cached by the WidgetsFactory as a texture for perf.
 */
export class ProgressBarWidgetRenderer {
  private widgets = new Map<string, WidgetState>();

  /** Per-widget design-store renderer (set only for widgets with a designId). */
  private designRenderers = new Map<string, ProgressBarDesignRenderer>();

  /**
   * Drop any prior state for this widget id — the design renderer (unsubscribes
   * its TimerService callback + frees its texture) AND a legacy graphics state
   * (unsubscribes its callback). Called at the top of every `render` so each
   * re-render starts clean and switching between the design and legacy paths
   * never leaves an orphaned subscription drawing onto a destroyed container.
   */
  private teardownWidget(id: string): void {
    const dr = this.designRenderers.get(id);
    if (dr) {
      dr.destroy();
      this.designRenderers.delete(id);
    }
    const state = this.widgets.get(id);
    if (state?.timerCallback) {
      TimerService.getInstance().unsubscribe(state.timerCallback);
    }
    this.widgets.delete(id);
  }

  render(
    widget: IStudioWidget,
    container: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const w = widget as IStudioWidgetProgressBar;
    const o = w.options;

    // Start clean: clear whatever this widget rendered last pass (either path).
    this.teardownWidget(widget.id);

    // ── Design-store path ──────────────────────────────────────────────────
    // When `options.designId` resolves to a catalog design, render its CARD SVG
    // (percent-driven) as a Pixi sprite at the widget bounds and skip the legacy
    // graphics bar entirely. Absent/unknown ⇒ fall through, unchanged.
    const design = getProgressBarDesign(o.designId);
    if (design) {
      const renderer = new ProgressBarDesignRenderer(design);
      this.designRenderers.set(widget.id, renderer);
      renderer.render(widget, container, destination);
      return;
    }

    const W = destination.width;
    const H = destination.height;
    const radius =
      o.borderRadius === undefined || o.borderRadius === "pill"
        ? H / 2
        : Math.max(0, o.borderRadius);

    // Outer container fill — border is drawn LAST (see end of render),
    // mirroring CSS's painting order where the element's border is
    // painted on top of its children. If we strokeed here, the track /
    // fill / gloss / label drawn below would cover the inside half of
    // the stroke and the container mask would clip the outside half,
    // leaving the border invisible.
    const bg = new Graphics();
    const bgFill = fillFromColor(o.backgroundColor, 0x111111);
    bg.roundRect(0, 0, W, H, radius).fill({
      color: bgFill.color,
      alpha: bgFill.alpha,
    });
    container.addChild(bg);

    // Clip everything inside the container to the bar's outer rounded rect
    // — mirrors the DOM renderer's `overflow: hidden` on the outer div so
    // a label longer than the bar gets clipped instead of overflowing into
    // the surrounding canvas.
    const clipMask = new Graphics();
    clipMask.roundRect(0, 0, W, H, radius).fill({ color: 0xffffff });
    container.addChild(clipMask);
    container.mask = clipMask;

    // Track.
    const track = new Graphics();
    const trackFill = fillFromColor(o.barBackgroundColor, 0x222222);
    track.roundRect(0, 0, W, H, radius).fill({
      color: trackFill.color,
      alpha: trackFill.alpha,
    });
    container.addChild(track);

    // Fill bar — redrawn on each tick (geometry only, solid colour).
    const fillBar = new Graphics();
    container.addChild(fillBar);

    // Gloss (Y2K look) — solid semi-transparent white pill confined to
    // the top 30% of the bar. Was a gradient previously; flattened for
    // CSS↔Pixi parity (see GLOSS_FILL constant above).
    let gloss: Graphics | undefined;
    if (o.showGloss) {
      gloss = new Graphics();
      container.addChild(gloss);
    }

    // Label.
    let labelText: Text | undefined;
    const fontFamily = o.fontFamily ?? "Inter, system-ui, sans-serif";
    const fontSize = o.fontSize ? parseInt(o.fontSize) : 20;
    const textColor = o.textColor ?? "#ffffff";
    const letterSpacing = (() => {
      const ls = o.letterSpacing;
      if (!ls) return 0.04 * fontSize;
      const m = ls.match(/^([\d.]+)(em|px)$/);
      if (!m) return 0;
      return m[2] === "em" ? parseFloat(m[1]) * fontSize : parseFloat(m[1]);
    })();
    if (o.label) {
      // All sizes in 1920×1080 design-space; the Pixi stage scales them.
      labelText = new Text({
        text: "",
        style: {
          fontFamily,
          fontSize,
          fill: textColor,
          // Numeric string weights match what the DOM renderer emits (CSS
          // accepts 700 as bold, 500 as medium). Using "bold" instead of
          // "700" forces some font stacks to a different cut and ends up
          // rendering at a slightly different metric than the DOM.
          fontWeight: o.bold ? "700" : "500",
          fontStyle: o.italic ? "italic" : "normal",
          letterSpacing,
          align: o.textAlign ?? "center",
        },
      });
      const align = o.textAlign ?? "center";
      // Pixi appends `letterSpacing` AFTER every glyph (including the last
      // one), while CSS only inserts it BETWEEN glyphs. The Pixi text's
      // bounding box is therefore one full `letterSpacing` wider than the
      // visually-rendered glyph run, which shifts the visual center LEFT
      // when we anchor at 0.5. Compensate by nudging x by half a letter-
      // spacing so the visible label sits at the same coordinate as the DOM.
      const trailingComp = letterSpacing / 2;
      if (align === "left") {
        labelText.anchor.set(0, 0.5);
        labelText.x = W * 0.05;
      } else if (align === "right") {
        labelText.anchor.set(1, 0.5);
        labelText.x = W * 0.95 + trailingComp;
      } else {
        labelText.anchor.set(0.5, 0.5);
        labelText.x = W / 2 + trailingComp;
      }
      labelText.y = H / 2;
      container.addChild(labelText as unknown as ContainerChild);
    }

    // Border — drawn LAST so it sits on top of fill / track / gloss /
    // label, matching CSS where the element's border is painted after
    // its children.
    //
    // Modeled as a roundRect-with-hole (fill + cut) instead of a stroke,
    // for exact parity with the DOM's CSS border. A Pixi `stroke()` is
    // anti-aliased and the `image-rendering: crisp-edges` on the
    // preview-live canvas discards the AA halo, which read ~1 CSS px
    // thinner than the equivalent solid CSS border in the Assembler.
    // The fill-and-cut approach uses two opaque fills with no AA on the
    // outer / inner edges that crisp-edges can shave off — the visible
    // ring matches the DOM border within a subpixel.
    if (o.borderWidth) {
      const borderFill = fillFromColor(o.borderColor, 0xffffff);
      const bw = o.borderWidth;
      const innerRadius = Math.max(0, radius - bw);
      const border = new Graphics();
      border
        .roundRect(0, 0, W, H, radius)
        .fill({ color: borderFill.color, alpha: borderFill.alpha })
        .roundRect(bw, bw, W - 2 * bw, H - 2 * bw, innerRadius)
        .cut();
      container.addChild(border);
    }

    const state: WidgetState = {
      destination,
      options: o,
      fillBar,
      gloss,
      labelText,
      lastRatio: -1,
    };
    this.widgets.set(widget.id, state);

    // Paint once with current value, then subscribe to TimerService so
    // the fill width animates as wall-clock time progresses between
    // `startDate` and `targetDate`.
    this.refresh(widget.id, Date.now());
    const callback = (nowMs: number) => this.refresh(widget.id, nowMs);
    state.timerCallback = callback;
    TimerService.getInstance().subscribe(callback);
  }

  private refresh(widgetId: string, nowMs: number): void {
    const state = this.widgets.get(widgetId);
    if (!state) return;
    const { options: o, destination, fillBar, gloss, labelText } = state;
    const W = destination.width;
    const H = destination.height;
    const radius =
      o.borderRadius === undefined || o.borderRadius === "pill"
        ? H / 2
        : Math.max(0, o.borderRadius);

    const max = Math.max(1, o.max ?? 100);
    const value = computeValue(o, max, nowMs);
    const ratio = value / max;
    const pct = Math.round(ratio * 100);

    // Skip redraw when nothing changed (cheap path for static / very slow ticks).
    if (Math.abs(ratio - state.lastRatio) > 0.001 || state.lastRatio === -1) {
      state.lastRatio = ratio;
      const fillW = W * ratio;
      fillBar.clear();
      if (ratio > 0) {
        const solid = fillFromColor(o.barColor, 0x22d3ee);
        fillBar.roundRect(0, 0, fillW, H, radius).fill({
          color: solid.color,
          alpha: solid.alpha,
        });
      }
      if (gloss) {
        gloss.clear();
        // Gloss spans the FULL bar (2%–98% of W), not just the fill —
        // matches the DOM's `left:2%; right:2%` on the outer container.
        // Confined to the top 10–40% of the bar height so it reads as a
        // sheen on the upper half rather than a fog over the whole pill.
        const glossW = W * 0.96;
        const glossH = H * 0.3;
        const glossX = W * 0.02;
        const glossY = H * 0.1;
        gloss
          .roundRect(glossX, glossY, glossW, glossH, glossH / 2)
          .fill(GLOSS_FILL);
      }
    }

    if (labelText) {
      labelText.text = formatLabel(
        o.label,
        Math.round(value),
        pct,
        o.showPercent,
      );
    }
  }

  destroy(): void {
    const timer = TimerService.getInstance();
    for (const state of this.widgets.values()) {
      if (state.timerCallback) timer.unsubscribe(state.timerCallback);
    }
    this.widgets.clear();

    for (const renderer of this.designRenderers.values()) {
      renderer.destroy();
    }
    this.designRenderers.clear();
  }
}
